import { useEffect, useRef, useSyncExternalStore } from 'react';

import { getRealtime, getUnread } from './api/communication';
import type { RealtimeConfig, Unread } from './api/communication';

/**
 * Le client temps réel de la communication (D67), sur le protocole Mercure.
 *
 *   1. `GET /api/communication/realtime` donne l'adresse du hub, un jeton
 *      d'abonné de courte durée et les sujets (le sujet privé du compte) ;
 *   2. un EventSource s'ouvre sur `hubUrl?topic=…&authorization=<jeton>` : le
 *      paramètre `authorization` est admis par la spécification Mercure, et
 *      EventSource ne sait pas poser d'en-tête ;
 *   3. le jeton est renouvelé une minute avant son expiration : un second flux
 *      s'ouvre, et l'ancien n'est fermé qu'une fois le nouveau établi ;
 *   4. un flux qui tombe est rouvert avec un délai croissant (1 s, 2 s, 4 s…
 *      jusqu'à une minute), en relisant un jeton neuf ;
 *   5. sans hub (`hubUrl` nul) ou tant que le flux est en panne, la cloche est
 *      tenue à jour en relisant `/api/communication/unread` toutes les 30 s.
 *
 * Les événements ne portent jamais le contenu d'un message (rien de privé ne
 * transite par le hub) : seulement `{type, ...ids}`, qui dit quoi relire.
 *
 * Le client est démarré par la cloche, dans la coquille, et arrêté à la
 * déconnexion (la coquille se démonte, et SessionProvider l'arrête aussi).
 */

export type RealtimeEvent =
    | { readonly type: 'chat.message'; readonly conversationId: string }
    | { readonly type: 'notification'; readonly id: string }
    | { readonly type: 'reply'; readonly notificationId: string; readonly recipientId: string }
    | { readonly type: 'alert'; readonly id: string }
    | { readonly type: 'read'; readonly count: number }
    | { readonly type: 'typing'; readonly conversationId: string; readonly accountId: string }
    /** Le repli : la relecture périodique des non lues ; `changed` dit si quelque chose a bougé. */
    | { readonly type: 'unread'; readonly unread: Unread; readonly changed: boolean }
    /** Le flux vient de s'ouvrir (ou de se rouvrir) : ce qui a pu être manqué est à relire. */
    | { readonly type: 'connected' };

export type RealtimeMode = 'idle' | 'live' | 'polling';

const POLL_EVERY = 30_000;
const RENEW_BEFORE = 60_000;
const BACKOFF_MAX = 60_000;

const listeners = new Set<(event: RealtimeEvent) => void>();
const modeListeners = new Set<() => void>();

let mode: RealtimeMode = 'idle';
let running = false;
let generation = 0;
let source: EventSource | null = null;
let attempts = 0;
let renewTimer: number | null = null;
let reconnectTimer: number | null = null;
let pollTimer: number | null = null;
let lastSignature: string | null = null;

function setMode(next: RealtimeMode): void {
    if (mode !== next) {
        mode = next;
        modeListeners.forEach((listener) => listener());
    }
}

function dispatch(event: RealtimeEvent): void {
    listeners.forEach((listener) => {
        try {
            listener(event);
        } catch {
            // Un abonné en faute ne prive pas les autres de l'événement.
        }
    });
}

function clear(timer: number | null): null {
    if (timer !== null) window.clearTimeout(timer);

    return null;
}

/** Les événements connus ; le reste est ignoré (un hub partagé peut en porter d'autres). */
function parse(data: string): RealtimeEvent | null {
    let value: unknown;

    try {
        value = JSON.parse(data);
    } catch {
        return null;
    }

    if (typeof value !== 'object' || value === null) return null;

    const event = value as Record<string, unknown>;
    const text = (name: string): string | null => (typeof event[name] === 'string' ? (event[name] as string) : null);

    switch (event.type) {
        case 'chat.message': {
            const conversationId = text('conversationId');

            return conversationId === null ? null : { type: 'chat.message', conversationId };
        }
        case 'notification':
        case 'alert': {
            const id = text('id');

            return id === null ? null : { type: event.type, id };
        }
        case 'reply': {
            const notificationId = text('notificationId');

            return notificationId === null ? null : { type: 'reply', notificationId, recipientId: text('recipientId') ?? '' };
        }
        case 'read':
            return { type: 'read', count: typeof event.count === 'number' ? event.count : 0 };
        case 'typing': {
            const conversationId = text('conversationId');
            const accountId = text('accountId');

            return conversationId === null || accountId === null ? null : { type: 'typing', conversationId, accountId };
        }
        default:
            return null;
    }
}

async function poll(gen: number): Promise<void> {
    try {
        const unread = await getUnread();

        if (gen !== generation) return;

        const signature = `${unread.count}:${unread.items.map((item) => `${item.kind}.${item.id}`).join(',')}`;
        const changed = lastSignature !== null && signature !== lastSignature;
        lastSignature = signature;
        dispatch({ type: 'unread', unread, changed });
    } catch {
        // Réseau coupé : on réessaiera au prochain tour. Une session morte est
        // signalée par le client d'API lui-même, qui reconduit à la connexion.
    }
}

function startPolling(gen: number): void {
    if (mode !== 'live') setMode('polling');

    if (pollTimer !== null) return;

    const tick = (): void => {
        if (gen !== generation) return;

        void poll(gen);
        pollTimer = window.setTimeout(tick, POLL_EVERY);
    };

    tick();
}

function stopPolling(): void {
    pollTimer = clear(pollTimer);
}

function scheduleReconnect(gen: number): void {
    if (reconnectTimer !== null || gen !== generation) return;

    const delay = Math.min(BACKOFF_MAX, 1000 * 2 ** attempts) + Math.round(Math.random() * 500);
    attempts += 1;
    reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        void connect(gen);
    }, delay);
}

function scheduleRenewal(config: RealtimeConfig, gen: number): void {
    renewTimer = clear(renewTimer);

    if (config.expiresAt === null) return;

    const expires = new Date(config.expiresAt).getTime();

    if (Number.isNaN(expires)) return;

    const delay = Math.max(15_000, expires - Date.now() - RENEW_BEFORE);
    renewTimer = window.setTimeout(() => {
        renewTimer = null;
        void renew(gen);
    }, delay);
}

async function renew(gen: number): Promise<void> {
    try {
        const config = await getRealtime();

        if (gen !== generation) return;

        if (config.hubUrl === null || config.token === null) {
            closeSource();
            startPolling(gen);

            return;
        }

        open(config, gen);
    } catch {
        if (gen !== generation) return;

        // Le jeton actuel vit encore une minute : on retente bientôt.
        renewTimer = window.setTimeout(() => {
            renewTimer = null;
            void renew(gen);
        }, 15_000);
    }
}

function closeSource(): void {
    source?.close();
    source = null;
}

function open(config: RealtimeConfig, gen: number): void {
    if (config.hubUrl === null || config.token === null) return;

    let url: URL;

    try {
        url = new URL(config.hubUrl, window.location.href);
    } catch {
        startPolling(gen);

        return;
    }

    for (const topic of config.topics) url.searchParams.append('topic', topic);
    url.searchParams.set('authorization', config.token);

    const next = new EventSource(url.toString());

    next.onopen = () => {
        if (gen !== generation) {
            next.close();

            return;
        }

        if (source !== null && source !== next) source.close();

        source = next;
        attempts = 0;
        stopPolling();
        setMode('live');
        scheduleRenewal(config, gen);
        dispatch({ type: 'connected' });
    };

    next.onmessage = (message: MessageEvent<string>) => {
        if (gen !== generation) return;

        const event = parse(message.data);

        if (event !== null) dispatch(event);
    };

    next.onerror = () => {
        if (gen !== generation) {
            next.close();

            return;
        }

        const current = source === next || source === null;

        if (next.readyState === EventSource.CLOSED) {
            next.close();

            if (current) {
                source = null;
                setMode('polling');
                startPolling(gen);
                scheduleReconnect(gen);
            } else {
                // Le renouvellement a échoué ; l'ancien flux tient encore.
                renewTimer = clear(renewTimer);
                renewTimer = window.setTimeout(() => {
                    renewTimer = null;
                    void renew(gen);
                }, 15_000);
            }

            return;
        }

        // CONNECTING : le navigateur réessaie de lui-même ; la cloche, elle,
        // continue d'être tenue à jour par la relecture périodique.
        if (current) {
            setMode('polling');
            startPolling(gen);
        }
    };

    if (source === null) source = next;
}

async function connect(gen: number): Promise<void> {
    let config: RealtimeConfig;

    try {
        config = await getRealtime();
    } catch {
        if (gen !== generation) return;

        startPolling(gen);
        scheduleReconnect(gen);

        return;
    }

    if (gen !== generation) return;

    if (config.hubUrl === null || config.token === null || config.topics.length === 0 || typeof EventSource === 'undefined') {
        // Pas de hub : la relecture périodique est le régime normal.
        startPolling(gen);

        return;
    }

    closeSource();
    open(config, gen);
}

/** Démarre le client (sans effet s'il tourne déjà). */
export function startRealtime(): void {
    if (running) return;

    running = true;
    generation += 1;
    attempts = 0;
    lastSignature = null;
    void connect(generation);
}

/** Arrête tout : flux, minuteries, relecture. Appelé à la déconnexion. */
export function stopRealtime(): void {
    running = false;
    generation += 1;
    closeSource();
    renewTimer = clear(renewTimer);
    reconnectTimer = clear(reconnectTimer);
    stopPolling();
    lastSignature = null;
    setMode('idle');
}

/** S'abonne aux événements ; rend la fonction de désabonnement. */
export function subscribe(listener: (event: RealtimeEvent) => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

export function realtimeMode(): RealtimeMode {
    return mode;
}

function subscribeMode(listener: () => void): () => void {
    modeListeners.add(listener);

    return () => {
        modeListeners.delete(listener);
    };
}

/** Le régime courant (en direct, relecture, arrêté), pour l'afficher. */
export function useRealtimeMode(): RealtimeMode {
    return useSyncExternalStore(subscribeMode, realtimeMode, realtimeMode);
}

/** Un écran s'abonne le temps d'être monté ; le gestionnaire peut changer à chaque rendu. */
export function useRealtime(handler: (event: RealtimeEvent) => void): void {
    const ref = useRef(handler);
    ref.current = handler;

    useEffect(() => subscribe((event) => ref.current(event)), []);
}
